/* src/RouteWrappers.js */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from './firebaseConfig';
import { useProfile } from './contexts/ProfileContext'; // Import useProfile

// Import required components
import ReplayViewer from './components/replay/ReplayViewer';
import JournalEntry from './components/journal/JournalEntry';
import LoadingSpinner from './components/ui/LoadingSpinner';
import ConfirmationModal from './components/ui/ConfirmationModal'; // Needed by Journal wrapper
import EditJournalEntryModal from './components/journal/EditJournalEntryModal'; // Needed by Journal wrapper

// --- Wrapper for PRIVATE Replays (/replay/:sessionId) ---
export const ReplayViewerWrapper = () => {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const {
        authUser, // Get authUser directly from context
        profiles: contextProfiles,
        isLoading: isLoadingContext // Tracks loading of auth state AND profiles list
    } = useProfile();
    const profiles = contextProfiles || [];

    // Initialize state with userId field
    const [sessionDetails, setSessionDetails] = useState({ replayData: null, username: 'Player', isPublic: false, profileId: null, userId: null });
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Wait for context loading (includes auth check)
        if (isLoadingContext) {
            console.log("[ReplayWrapper] Waiting for context loading...");
            // Keep loading state true while context loads
            if (!isLoadingData) setIsLoadingData(true);
            return;
        }

        // Explicitly check for authUser *after* loading is complete
        if (!authUser) {
            console.log("[ReplayWrapper] Context loaded, but no authUser found.");
            setError("Authentication required to view this replay.");
            setIsLoadingData(false); // Stop data loading attempt
            return;
        }

        if (!sessionId) {
            setError("Session ID missing.");
            setIsLoadingData(false);
            return;
        }

        // Ensure profiles are available (should be if isLoadingContext is false and authUser exists)
        if (!Array.isArray(profiles)) {
             // Added check: If context isn't loading but profiles aren't an array, it's an error state
             console.error("[ReplayWrapper] Profile data is not an array after context loaded.");
             setError("Error loading profile data.");
             setIsLoadingData(false);
             return;
        }
        // Note: If profiles is an empty array, the loop below will handle it correctly.

        const fetchReplay = async () => {
            // Reset state for fetching
            setIsLoadingData(true);
            setError(null);
            let found = null;
            let fetchErr = null;
            const userUid = authUser.uid; // Get UID directly

            console.log(`[ReplayWrapper] Fetching replay for user ${userUid}, session ${sessionId}`);

            for (const p of profiles) {
                if (!p?.id) {
                     console.warn("[ReplayWrapper] Skipping profile with no ID:", p);
                     continue; // Skip profiles without an ID
                }
                try {
                    const ref = doc(db, 'users', userUid, 'profiles', p.id, 'sessions', sessionId);
                    const snap = await getDoc(ref);
                    if (snap.exists()) {
                        const d = snap.data();
                        // --- Ensure replayData structure and startTime are valid ---
                        if (d.replayData?.settings && Array.isArray(d.replayData.events) && d.replayData.startTime !== undefined) {
                             const startTime = d.replayData.startTime instanceof Timestamp
                                ? d.replayData.startTime.toMillis()
                                : (typeof d.replayData.startTime === 'number' ? d.replayData.startTime : Date.parse(d.replayData.startTime));

                            if (typeof startTime === 'number' && !isNaN(startTime)) {
                                // --- MODIFICATION: Explicitly add userId to replayData passed down ---
                                // This ensures ReplayViewer has the correct ID for 'viewingOwnReplay' check
                                const replayDataWithUser = {
                                    ...d.replayData,
                                    startTime,
                                    userId: userUid // Add the authenticated user's ID here
                                };
                                found = {
                                    replayData: replayDataWithUser,
                                    username: d.username || p.username || 'Your',
                                    isPublic: d.isPublic || false, // Get public status from session doc
                                    profileId: p.id,
                                    userId: userUid // Also keep userId at the top level if needed elsewhere
                                };
                                console.log("[ReplayWrapper] Replay found in profile:", p.id);
                                break; // Exit loop once found
                            } else {
                                console.warn(`[ReplayWrapper] Invalid startTime in session ${sessionId} for profile ${p.id}`);
                            }
                        } else {
                            console.warn(`[ReplayWrapper] Invalid replayData structure in session ${sessionId} for profile ${p.id}`);
                        }
                    } else {
                         // This is expected if the session belongs to a different profile
                         // console.log(`[ReplayWrapper] Session ${sessionId} not found in profile ${p.id}`);
                    }
                } catch (e) {
                    fetchErr = "Error fetching replay data.";
                    console.error(`[ReplayWrapper] Error fetching ${sessionId} for profile ${p.id}:`, e);
                    // Optional: break here if you want to stop on first error, or continue searching other profiles
                }
            } // End profile loop

            if (found) {
                setSessionDetails(found);
            } else {
                setError(fetchErr || "Replay not found in your profiles or access denied.");
                console.log(`[ReplayWrapper] Replay ${sessionId} not found for user ${userUid} across ${profiles.length} profiles checked.`);
            }
            setIsLoadingData(false);
        };

        // Only fetch if we haven't already found an error (like missing auth)
        if (!error) {
            fetchReplay();
        }

    // Depend on context state and route parameters
    }, [sessionId, authUser, profiles, isLoadingContext, navigate, error]); // Added error as dep to prevent refetch loop on auth error

    // Combined loading state check
    if (isLoadingContext || isLoadingData) {
        return <LoadingSpinner message="Loading Your Replay..." />;
    }

    // Error handling after loading
    if (error) {
        return <div className="p-4 text-red-600 text-center">{error} <button onClick={() => navigate('/dashboard')} className='text-blue-500 underline ml-2'>Back</button></div>;
    }

    // Handle case where data loading finished but replayData is still null (not found)
    if (!sessionDetails.replayData) {
        // If no error was set but data is missing, set a generic not found error
        return <div className="p-4 text-gray-500 text-center">Could not load replay data. It might not exist or belong to this profile. <button onClick={() => navigate('/dashboard')} className='text-blue-500 underline ml-2'>Back</button></div>;
    }

    // Render ReplayViewer only when data is ready and no error
    // Pass authUser explicitly, ensures ReplayViewer gets the correct user state
    return (
        <ReplayViewer
            replayData={sessionDetails.replayData} // Now includes userId
            username={sessionDetails.username}
            sessionId={sessionId}
            profileId={sessionDetails.profileId}
            initialIsPublic={sessionDetails.isPublic} // Pass the fetched public status
            // --- Pass isPublicView={false} as this is the private route wrapper ---
            isPublicView={false}
            isLocalReplay={false}
            onExit={() => navigate('/dashboard')}
            authUser={authUser} // Pass the confirmed authUser object
        />
    );
};

// --- Wrapper for PUBLIC Replays (/share/replay/:sessionId) ---
// No changes were needed here for the fix
export const PublicReplayViewerWrapper = () => {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const { authUser } = useProfile();
    const [docData, setDocData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!sessionId) { setError("Invalid replay link."); setIsLoading(false); return; }
        const fetchPublicReplay = async () => {
            setIsLoading(true); setError(null);
            const ref = doc(db, 'publicReplays', sessionId);
            try {
                const snap = await getDoc(ref);
                if (snap.exists()) {
                    const d = snap.data();
                    if (d.replayData?.settings && Array.isArray(d.replayData.events) && d.replayData.startTime !== undefined) {
                        const startTime = d.replayData.startTime instanceof Timestamp ? d.replayData.startTime.toMillis() : (typeof d.replayData.startTime === 'number' ? d.replayData.startTime : Date.parse(d.replayData.startTime));
                        if (typeof startTime === 'number' && !isNaN(startTime)) {
                            setDocData({ ...d, replayData: { ...d.replayData, startTime, userId: d.userId } }); // Include userId from public doc
                        } else { throw new Error("Invalid startTime format."); }
                    } else { throw new Error("Invalid public replay structure."); }
                } else { setError("Public replay not found."); }
            } catch (e) { setError(`Error loading replay: ${e.message}`); }
            finally { setIsLoading(false); }
        };
        fetchPublicReplay();
    }, [sessionId]);

    if (isLoading) return <LoadingSpinner message="Loading Public Replay..." />;
    if (error) return <div className="p-4 text-red-600 text-center">{error} <button onClick={() => navigate('/')} className='text-blue-500 underline ml-2'>Home</button></div>;
    if (!docData?.replayData) return <div className="p-4 text-gray-500 text-center">Invalid replay data loaded. <button onClick={() => navigate('/')} className='text-blue-500 underline ml-2'>Home</button></div>;

    return (
        <ReplayViewer
            replayData={docData.replayData} // Includes userId if present in public doc
            username={docData.username || 'Player'}
            // --- Pass isPublicView={true} as this is the public route wrapper ---
            isPublicView={true}
            isLocalReplay={false}
            sessionId={sessionId}
            profileId={docData.profileId || null}
            initialIsPublic={true}
            authUser={authUser} // Pass viewer's auth state
        />
    );
};


// --- Wrapper for Journal Entry View ---
// No changes needed here
export const JournalEntryViewerWrapper = () => {
    const { entryId } = useParams();
    const navigate = useNavigate();
    const {
        authUser,
        profiles: contextProfiles,
        isLoading: isLoadingContext,
        updateJournalEntry,
        removeJournalEntry
    } = useProfile();
    const profiles = contextProfiles || [];

    const [entryData, setEntryData] = useState(null);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteConfirmInput, setDeleteConfirmInput] = useState('');

    useEffect(() => {
        if (isLoadingContext) return;
        if (!authUser) { setError("Authentication required."); setIsLoadingData(false); return; }
        if (!entryId) { setError("Entry ID missing."); setIsLoadingData(false); return; }
        if (!Array.isArray(profiles)) { setError("Profile data error."); setIsLoadingData(false); return; }

        const fetchEntry = async () => {
            setIsLoadingData(true); setError(null);
            let found = null; let fetchErr = null; let profileIdForEntry = null;
            for (const p of profiles) {
                if (!p?.id) continue;
                try {
                    const ref = doc(db, 'users', authUser.uid, 'profiles', p.id, 'journal', entryId);
                    const snap = await getDoc(ref);
                    if (snap.exists()) {
                        const d = snap.data();
                        const toISO = (ts) => {
                           if (ts instanceof Timestamp) return ts.toDate().toISOString(); if (typeof ts === 'string' || typeof ts === 'number') { try { const date = new Date(ts); if (!isNaN(date)) return date.toISOString(); } catch(e) {} } return null;
                        };
                        found = { id: snap.id, ...d, timestamp: toISO(d.timestamp), updatedAt: toISO(d.updatedAt), snapshotData: d.snapshotData ? { ...d.snapshotData, snapshotTimestamp: toISO(d.snapshotData.snapshotTimestamp), chartData: Array.isArray(d.snapshotData.chartData) ? d.snapshotData.chartData.map(point => ({...point, date: toISO(point.date)})) : [] } : null, summaryData: d.summaryData ? { ...d.summaryData, date: toISO(d.summaryData.date), startTime: toISO(d.summaryData.startTime) } : null };
                        profileIdForEntry = p.id;
                        break;
                    }
                } catch (e) { fetchErr = "Error fetching entry."; console.error(`Error fetching journal ${entryId}:`, e); }
            }
            if (found) { setEntryData({ ...found, _profileId: profileIdForEntry }); }
            else { setError(fetchErr || "Journal entry not found or access denied."); }
            setIsLoadingData(false);
        };
        fetchEntry();
    }, [entryId, authUser, profiles, isLoadingContext]);

    const handleEdit = () => setIsEditing(true);
    const handleCancelEdit = () => setIsEditing(false);
    const handleSaveEdit = async (id, data) => {
        if (!entryData?._profileId) { alert("Save failed: Missing profile ID."); return false; }
        const ok = await updateJournalEntry(entryData._profileId, id, data);
        if (ok) { setIsEditing(false); setEntryData(prev => ({ ...prev, ...data, updatedAt: new Date().toISOString() })); }
        else { alert("Save failed."); }
        return ok;
    };

    const handleDeleteReq = () => { setDeleteConfirmInput(''); setShowDeleteConfirm(true); };
    const handleCancelDel = () => setShowDeleteConfirm(false);
    const handleConfirmDel = async () => {
        const req = "delete";
        if (deleteConfirmInput.trim().toLowerCase() !== req) { alert(`Type '${req}' to confirm.`); return; }
        if (!entryData?._profileId || !entryData?.id || isDeleting) return;
        setIsDeleting(true);
        const ok = await removeJournalEntry(entryData._profileId, entryData.id);
        setIsDeleting(false); setShowDeleteConfirm(false);
        if (ok) { navigate('/journal', { replace: true }); }
        else { alert("Delete failed."); }
    };

    if (isLoadingContext || isLoadingData) return <LoadingSpinner message="Loading Journal Entry..." />;
    if (error) return <div className="p-4 text-red-600 text-center">{error} <button onClick={() => navigate('/journal')} className='text-blue-500 underline ml-2'>Back</button></div>;
    if (!entryData) return <div className="p-4 text-gray-500 text-center">Cannot load journal entry. <button onClick={() => navigate('/journal')} className='text-blue-500 underline ml-2'>Back</button></div>;

    return (
        <div className="p-4 sm:p-6 bg-gray-100 min-h-screen">
            <div className="max-w-2xl mx-auto">
                <div className="mb-4"><button onClick={() => navigate('/journal')} className="text-sm text-indigo-600 hover:underline">← Back to Journal List</button></div>
                <JournalEntry entry={entryData} onEdit={handleEdit} onDelete={handleDeleteReq} onNavigate={navigate} />
                <EditJournalEntryModal isOpen={isEditing} entry={entryData} onSave={handleSaveEdit} onCancel={handleCancelEdit} />
                <ConfirmationModal isOpen={showDeleteConfirm} title="Delete Journal Entry" message={`Type 'delete' to confirm:`} onConfirm={handleConfirmDel} onCancel={handleCancelDel} confirmText={isDeleting ? "Deleting..." : "Delete"} isDestructive={true} requiredInputText="delete" inputLabel="Type 'delete' to confirm" inputValue={deleteConfirmInput} onInputChange={(e) => setDeleteConfirmInput(e.target.value)} />
            </div>
        </div>
    );
};